import { OpusAgentModel } from "@src/types.generated";

// Human names for the closed set of Opus Agent models, keyed by the enum so a
// new member is a type error until it gets a name. The order is the picker's.
export const OPUS_AGENT_MODEL_LABELS: Record<OpusAgentModel, string> = {
  [OpusAgentModel.ClaudeOpus_5]: "Claude Opus 5",
  [OpusAgentModel.ClaudeSonnet_5]: "Claude Sonnet 5",
  [OpusAgentModel.ClaudeHaiku_4_5]: "Claude Haiku 4.5",
  [OpusAgentModel.ClaudeFable_5_1]: "Claude Fable 5.1",
  [OpusAgentModel.Gpt_5_6Sol]: "GPT-5.6 Sol",
  [OpusAgentModel.Gpt_5_6Terra]: "GPT-5.6 Terra",
  [OpusAgentModel.Gpt_5_6Luna]: "GPT-5.6 Luna",
  [OpusAgentModel.Gpt_6Astra]: "GPT-6 Astra",
};

export const OPUS_AGENT_MODEL_ORDER: readonly OpusAgentModel[] = [
  OpusAgentModel.ClaudeOpus_5,
  OpusAgentModel.ClaudeSonnet_5,
  OpusAgentModel.ClaudeHaiku_4_5,
  OpusAgentModel.ClaudeFable_5_1,
  OpusAgentModel.Gpt_6Astra,
  OpusAgentModel.Gpt_5_6Sol,
  OpusAgentModel.Gpt_5_6Terra,
  OpusAgentModel.Gpt_5_6Luna,
];

export const opusAgentModelLabel = (model: OpusAgentModel): string =>
  OPUS_AGENT_MODEL_LABELS[model];

// Members withheld from the picker for now. Fable is unverified against a
// real API key (30-day-retention requirement, forced-tool_choice
// sensitivity). Labels and ratings still cover every member: a chat whose
// stored model is hidden keeps showing it — it just isn't offered.
const HIDDEN_OPUS_AGENT_MODELS: ReadonlySet<OpusAgentModel> = new Set([
  OpusAgentModel.ClaudeFable_5_1,
]);

export const isOpusAgentModelAvailable = (model: OpusAgentModel): boolean =>
  !HIDDEN_OPUS_AGENT_MODELS.has(model);

// The ordered list the menu renders.
export const OPUS_AGENT_PICKER_MODELS: readonly OpusAgentModel[] =
  OPUS_AGENT_MODEL_ORDER.filter(isOpusAgentModelAvailable);

// Relative speed/intelligence on a 1–5 scale, keyed by the enum so a new
// member is a type error until rated. Claude tiers by product positioning;
// the GPT-5.6 tier per its published positioning (Luna fast/lighter, Terra
// balanced, Sol most capable); GPT-6 Astra is the newer-generation flagship
// and outranks Sol and Opus on intelligence.
export type OpusAgentModelRatingValue = 1 | 2 | 3 | 4 | 5;
export const OPUS_AGENT_MODEL_RATING_MAX: OpusAgentModelRatingValue = 5;

export type OpusAgentModelRating = {
  speed: OpusAgentModelRatingValue;
  intelligence: OpusAgentModelRatingValue;
};

export const OPUS_AGENT_MODEL_RATINGS: Record<
  OpusAgentModel,
  OpusAgentModelRating
> = {
  [OpusAgentModel.ClaudeOpus_5]: { speed: 2, intelligence: 4 },
  [OpusAgentModel.ClaudeSonnet_5]: { speed: 4, intelligence: 3 },
  [OpusAgentModel.ClaudeHaiku_4_5]: { speed: 5, intelligence: 1 },
  [OpusAgentModel.ClaudeFable_5_1]: { speed: 1, intelligence: 5 },
  [OpusAgentModel.Gpt_5_6Sol]: { speed: 2, intelligence: 4 },
  [OpusAgentModel.Gpt_5_6Terra]: { speed: 4, intelligence: 3 },
  [OpusAgentModel.Gpt_5_6Luna]: { speed: 5, intelligence: 1 },
  [OpusAgentModel.Gpt_6Astra]: { speed: 1, intelligence: 5 },
};
